/**
 * "@" in the chat input: a word starting with @ right before the caret opens a list of what
 * the user may point the answer at. Picking a skill or knowledge group turns it on for the
 * conversation (as the + menu does); a project document or Microsoft 365 source stays as
 * "@name" in the question, which tells the model what to read with its tools.
 */
export type MentionKind = 'skill' | 'knowledge' | 'document' | 'microsoft';

export type MentionItem = {
  kind: MentionKind;
  /** Skill key, knowledge group slug, document id or Microsoft source id. */
  key: string;
  label: string;
  detail?: string | undefined;
  /** Already on for this conversation (skills and knowledge). */
  active?: boolean | undefined;
};

export const MENTION_KINDS: MentionKind[] = ['skill', 'knowledge', 'document', 'microsoft'];
const MAX_PER_KIND = 5;

/** The "@word" being typed right before the caret, or null when the caret is not in one. */
export function mentionQuery(
  value: string,
  caret: number,
): { start: number; query: string } | null {
  const before = value.slice(0, caret);
  const match = /(^|\s)@([^\s@]{0,40})$/.exec(before);
  const query = match?.[2];
  if (query === undefined) return null;
  return { start: before.length - query.length - 1, query };
}

/** Items matching the query, best first, a few per kind, in the order of MENTION_KINDS. */
export function filterMentions(items: MentionItem[], query: string): MentionItem[] {
  const q = query.toLowerCase();
  const score = (item: MentionItem) => {
    const label = item.label.toLowerCase();
    if (!q) return 1;
    if (label.startsWith(q)) return 3;
    if (label.split(/[\s\-_.]+/).some((word) => word.startsWith(q))) return 2;
    return `${label} ${item.detail?.toLowerCase() ?? ''}`.includes(q) ? 1 : 0;
  };
  return MENTION_KINDS.flatMap((kind) =>
    items
      .filter((item) => item.kind === kind)
      .map((item) => ({ item, score: score(item) }))
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label))
      .slice(0, MAX_PER_KIND)
      .map(({ item }) => item),
  );
}

/** The text with "@query" replaced by "@Label ", and where the caret goes after it. */
export function insertMention(
  value: string,
  at: { start: number; query: string },
  label: string,
): { value: string; caret: number } {
  const end = at.start + 1 + at.query.length;
  const inserted = `@${label} `;
  const rest = value.slice(end).replace(/^ /, '');
  return { value: value.slice(0, at.start) + inserted + rest, caret: at.start + inserted.length };
}
